import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  Link as MuiLink,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight'
import axios from 'axios'
import { formatDistanceToNowStrict } from 'date-fns'
import { Link as RouterLink, useSearchParams } from 'react-router-dom'
import QuestionDisplay from '../../components/QuestionDisplay'
import QuestionManager from '../../components/QuestionManager'
import { StructuredQuestion } from '../../interfaces/questions'

import { useAuth0 } from '@/utils/auth/auth0'
import {
  AMOUNT_RANGE_KEYS,
  extractAmountRange,
  formatAmountRange,
} from './amountFields'
import { AdvisoryDetail } from './AdvisoryDetail'
import { RegenPanel } from './RegenPanel'
import { FunderRegenPanel } from './FunderRegenPanel'
import { MergeSummary } from './MergeSummary'
import { RoundDeleteSummary } from './RoundDeleteSummary'
import { formatLongDateNZ } from '../../utils/formatDate'


// Helpers, types, and sub-components live in `_shared` (pure helpers)
// and `_shared-components` (React components / JSX render helpers) so
// the same code powers both this page and the inline ProposedEditsPanel
// rendered on funder/opportunity show pages.
import {
  apiUrl,
  type EditStatus,
  diffColumnLabels,
  type ResourceType,
  type EditAction,
  type ProposedEdit,
  type BatchGroup,
  groupByBatch,
  type ListResponse,
  type FunderOption,
  targetHref,
  mergeSurvivorDisplay,
  targetDisplay,
  statusColor,
  actionColor,
  type FieldDef,
  fieldDefFor,
  validatePayload,
  normalizeDate,
  FIELD_LABELS,
  labelFor,
  formatValue,
  type DiffRow,
  buildDiff,
  fetchCurrent,
  buildFundingRoundBefore,
  questionsFromPayload,
} from './_shared'
import {
  AmendForm,
  InlineDiffCell,
  QuestionsAmendForm,
  QuestionsPreview,
  renderDiffCell,
  SafeExternalLink,
} from './_shared-components'
import { EditReviewModal } from './EditReviewModal'

type SortOrder = 'newest' | 'oldest'

export const ProposedEditsList: React.FC = () => {
  const [statusFilter, setStatusFilter] = useState<string>('pending')
  const [resourceFilter, setResourceFilter] = useState<string>('')
  // International filter (GH#1672): '' = all, 'true' = international funders,
  // 'false' = domestic. Funder-level and unit-safe on the backend.
  const [internationalFilter, setInternationalFilter] = useState<string>('')
  // GH#1584: sort whole batches/edits by their proposed-edit date. Sorting is
  // batch-safe on the backend (units are ordered by their earliest edit), so a
  // batch is never split. GH#1567: look up an edit (and its batch) by ID.
  const [sortOrder, setSortOrder] = useState<SortOrder>('newest')
  const [idSearch, setIdSearch] = useState<string>('')
  const [idSearchActive, setIdSearchActive] = useState<string>('')
  // GH#1604: search the queue by opportunity name. Like the ID search, the term
  // is committed on Enter (oppNameActive) so we don't refetch on every keystroke.
  // Composes with the status/resource/international/funder filters, but is dropped
  // in ID-search mode (which ignores all filters).
  const [oppNameSearch, setOppNameSearch] = useState<string>('')
  const [oppNameActive, setOppNameActive] = useState<string>('')
  const [funderFilter, setFunderFilter] = useState<FunderOption | null>(null)
  const [funderOptions, setFunderOptions] = useState<FunderOption[]>([])
  const [items, setItems] = useState<ProposedEdit[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(50)
  const [loading, setLoading] = useState(true)
  const [selected, setSelected] = useState<ProposedEdit | null>(null)
  const [reviewComment, setReviewComment] = useState('')
  const [reviewing, setReviewing] = useState(false)
  const [chain, setChain] = useState<ProposedEdit[]>([])
  const [error, setError] = useState<string | null>(null)
  // GH#1673: SSE-signalled changes raise this nudge instead of auto-reloading,
  // so a new batch (or another admin's action) never refreshes the list out
  // from under an active reviewer.
  const [pendingUpdates, setPendingUpdates] = useState(false)
  // After the reviewer's OWN action we background-reload the list ourselves;
  // the server also broadcasts that change back over SSE, so swallow those
  // echoes for a short window to avoid a spurious "updates available" nudge.
  const suppressSseUntilRef = useRef(0)
  const [currentRecord, setCurrentRecord] = useState<Record<string, unknown> | null>(
    null
  )
  const [loadingCurrent, setLoadingCurrent] = useState(false)
  const [batchView, setBatchView] = useState<BatchGroup | null>(null)
  const [batchCurrents, setBatchCurrents] = useState<
    Record<number, Record<string, unknown> | null>
  >({})
  const [batchCurrentsLoading, setBatchCurrentsLoading] = useState(false)
  const [batchConfirm, setBatchConfirm] = useState<'approve' | 'reject' | null>(
    null
  )
  const [batchComment, setBatchComment] = useState('')
  const [batchWorking, setBatchWorking] = useState(false)
  const [expandedBatches, setExpandedBatches] = useState<Set<string>>(new Set())
  const [itemWorkingId, setItemWorkingId] = useState<number | null>(null)
  const [amendingId, setAmendingId] = useState<number | null>(null)
  const [amendDraft, setAmendDraft] = useState<Record<string, unknown>>({})
  const [amendSaving, setAmendSaving] = useState(false)
  const { getAccessTokenSilently } = useAuth0()

  // Support deep-link from inline panels on the funder/opportunity pages:
  // /proposed-edits?id=N opens the review modal for edit N on mount. Lets
  // the inline ProposedEditsPanel hand off to the full-detail view without
  // duplicating the diff/payload rendering inside the panel.
  const [searchParams, setSearchParams] = useSearchParams()
  useEffect(() => {
    const idParam = searchParams.get('id')
    if (!idParam) return
    const editId = Number(idParam)
    if (!Number.isFinite(editId)) return
    let cancelled = false
    ;(async () => {
      try {
        const res = await axios.get<ProposedEdit>(
          `${apiUrl}/proposed-edits/${editId}`,
        )
        if (cancelled) return
        setSelected(res.data)
        // Strip ?id=N from the URL so navigating away from the modal then
        // hitting the same URL doesn't immediately re-open it.
        const next = new URLSearchParams(searchParams)
        next.delete('id')
        setSearchParams(next, { replace: true })
      } catch (e) {
        console.warn(`Failed to deep-link to proposed-edit #${editId}`, e)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  // GH#1673: a "background" reload skips the loading spinner so the <Table>
  // stays mounted — rows reconcile in place under their stable keys, the page
  // height never collapses, and the reviewer's scroll position is preserved.
  // Foreground reloads (initial load, filter change, manual Refresh) still show
  // the spinner.
  const load = useCallback(async (opts?: { background?: boolean }) => {
    const background = opts?.background ?? false
    if (!background) setLoading(true)
    setError(null)
    // A real fetch catches the list up to the server, so any "updates
    // available" nudge is now stale.
    setPendingUpdates(false)
    try {
      const params = new URLSearchParams()
      // ID search (GH#1567) is a troubleshooting lookup: the backend returns
      // the matching edit(s) plus their full batch context and ignores the
      // status/resource/funder filters, so we send only `ids` in that mode.
      if (idSearchActive) {
        params.append('ids', idSearchActive)
      } else {
        if (statusFilter) params.append('status', statusFilter)
        if (resourceFilter) params.append('resource_type', resourceFilter)
        if (internationalFilter)
          params.append('is_international', internationalFilter)
        if (funderFilter) params.append('funder_uuid', funderFilter.uuid)
        if (oppNameActive) params.append('opportunity_name', oppNameActive)
      }
      params.append('sort', sortOrder)
      // limit/offset count whole batches (units), not edit rows — the backend
      // never splits a batch across a page (GH#1566). See
      // _ai/analysis/iter24-pr4-review-queue-list.md for the contract.
      params.append('limit', String(rowsPerPage))
      params.append('offset', String(page * rowsPerPage))
      const res = await axios.get<ListResponse>(
        `${apiUrl}/proposed-edits/?${params.toString()}`
      )
      setItems(res.data.items)
      setTotal(res.data.total)
    } catch (e: any) {
      setError(e?.response?.data?.detail ?? e?.message ?? 'Failed to load')
    } finally {
      if (!background) setLoading(false)
    }
  }, [
    statusFilter,
    resourceFilter,
    internationalFilter,
    funderFilter,
    sortOrder,
    idSearchActive,
    oppNameActive,
    page,
    rowsPerPage,
  ])

  // When filters change while the user is past page 0, we need to reset the
  // page back to 0 so the server-side OFFSET stays in range. Naively running
  // both effects below in sequence races the load: the first render fires
  // load() with the new filters at the OLD offset, then setPage(0) re-renders
  // and fires a second load() at offset 0 — two requests, with the older one
  // potentially resolving last and rendering stale rows. The skip-ref below
  // suppresses that first stale load and lets the post-setPage render fetch.
  const skipNextLoadRef = useRef(false)

  useEffect(() => {
    if (page !== 0) {
      skipNextLoadRef.current = true
      setPage(0)
    }
    // page intentionally omitted from deps: this effect responds to filter
    // changes, not to page navigation. Including page would cause a loop
    // because we call setPage(0) inside the body.
  }, [
    statusFilter,
    resourceFilter,
    internationalFilter,
    funderFilter,
    sortOrder,
    idSearchActive,
    oppNameActive,
  ])

  useEffect(() => {
    if (skipNextLoadRef.current) {
      skipNextLoadRef.current = false
      return
    }
    load()
  }, [load])

  useEffect(() => {
    // Subscribe to proposed-edit change events so the list stays in sync when
    // another admin reviews/amends/rejects something. EventSource can't send
    // an Authorization header, so we pass the JWT as a query param — matches
    // the pattern used by the scan-progress SSE endpoint.
    let es: EventSource | null = null
    let cancelled = false
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null

    const open = async () => {
      try {
        const token = await getAccessTokenSilently()
        if (cancelled) return
        const url = `${apiUrl}/proposed-edits/stream?token=${encodeURIComponent(token)}`
        es = new EventSource(url)
        es.onmessage = () => {
          // GH#1673: do NOT refetch the list out from under an active
          // reviewer. A background change (another admin, or a db-checkback
          // pushing a new batch) raises a non-disruptive "updates available"
          // nudge; the reviewer refreshes when ready.
          // ponytail: suppressSseUntil is a time-window heuristic that swallows
          // the echo of the reviewer's OWN just-completed action (the server
          // broadcasts it back). A delayed echo past the window can still raise
          // a spurious nudge — harmless, since refreshing is always safe.
          // Actor-tagged events would be the robust fix if this proves annoying.
          if (Date.now() < suppressSseUntilRef.current) return
          setPendingUpdates(true)
        }
        es.onerror = () => {
          // Browser will auto-reconnect for transient failures, but if the
          // stream is definitively closed we nudge it back open after a
          // short delay.
          if (es && es.readyState === EventSource.CLOSED) {
            es.close()
            es = null
            if (!cancelled) {
              reconnectTimer = setTimeout(open, 3000)
            }
          }
        }
      } catch (err) {
        // Auth failure shouldn't break the page — silently skip realtime.
        console.warn('proposed-edits SSE: failed to open', err)
      }
    }

    open()
    return () => {
      cancelled = true
      if (reconnectTimer) clearTimeout(reconnectTimer)
      if (es) es.close()
    }
  }, [load, getAccessTokenSilently])

  useEffect(() => {
    axios
      .get<FunderOption[]>(`${apiUrl}/funders/options/`)
      .then((res) => setFunderOptions(res.data))
      .catch((e) => console.error('Failed to load funder options', e))
  }, [])

  useEffect(() => {
    if (
      !selected ||
      selected.action !== 'update' ||
      selected.resource_type === 'advisory'
    ) {
      setCurrentRecord(null)
      return
    }
    if (selected.resource_type === 'funding_round') {
      // funding_round edits have target_uuid=null (round id is in payload).
      // The proposed_edits API enriches the row with target_round_*_date so
      // we can build the before-record directly without an extra fetch.
      setCurrentRecord(buildFundingRoundBefore(selected))
      return
    }
    if (!selected.target_uuid) {
      setCurrentRecord(null)
      return
    }
    setLoadingCurrent(true)
    fetchCurrent(selected.resource_type, selected.target_uuid)
      .then((data) => setCurrentRecord(data))
      .finally(() => setLoadingCurrent(false))
  }, [selected])

  useEffect(() => {
    if (!selected) {
      setChain([])
      return
    }
    let cancelled = false
    axios
      .get<{ items: ProposedEdit[] }>(
        `${apiUrl}/proposed-edits/${selected.id}/chain`,
      )
      .then((res) => {
        if (!cancelled) setChain(res.data.items)
      })
      .catch(() => {
        if (!cancelled) setChain([])
      })
    return () => {
      cancelled = true
    }
  }, [selected])

  const review = async (action: 'approve' | 'reject') => {
    if (!selected) return
    setReviewing(true)
    try {
      const body: Record<string, unknown> = {}
      if (reviewComment) body.comment = reviewComment
      await axios.post(
        `${apiUrl}/proposed-edits/${selected.id}/${action}`,
        body,
      )
      setSelected(null)
      setReviewComment('')
      suppressSseUntilRef.current = Date.now() + 5000
      // GH#1673: background reload keeps the reviewer's scroll/place.
      await load({ background: true })
    } catch (e: any) {
      const status = e?.response?.status
      const detail = e?.response?.data?.detail
      if (status === 409) {
        // 409 = the edit could not be applied as-is: either another admin got
        // here first, or the create duplicates an existing opportunity
        // (GH#1622). Close the detail pane, refresh so the row shows its true
        // state, then surface the server detail AFTER the reload (load() clears
        // `error`, so setting it last keeps the message visible in the toast).
        setSelected(null)
        setReviewComment('')
        suppressSseUntilRef.current = Date.now() + 5000
        await load({ background: true })
        setError(detail ?? 'This edit was already reviewed by another admin.')
      } else {
        setError(detail ?? e?.message ?? 'Review failed')
      }
    } finally {
      setReviewing(false)
    }
  }

  const diffRows = useMemo<DiffRow[]>(() => {
    if (!selected || selected.resource_type === 'advisory') return []
    if (selected.action === 'merge') return []
    if (selected.resource_type === 'funding_round' && selected.action === 'delete') return []
    const before = selected.action === 'update' ? currentRecord : null
    return buildDiff(before, selected.payload, selected.resource_type)
  }, [selected, currentRecord])

  const groups = useMemo<BatchGroup[]>(() => groupByBatch(items), [items])

  /**
   * A merge deletes a record for good, and the impact report that says what
   * it moves and discards is only fetched in the review modal. Approving one
   * from a batch would apply it sight unseen.
   */
  const batchHasPendingMerge = useMemo(
    () =>
      (batchView?.items ?? []).some(
        (i) => i.status === 'pending' && i.action === 'merge',
      ),
    [batchView],
  )

  useEffect(() => {
    if (!batchView || !batchView.batchId) return
    const refreshed = groups.find((g) => g.batchId === batchView.batchId)
    if (refreshed && refreshed !== batchView) {
      setBatchView(refreshed)
    }
  }, [groups, batchView])

  useEffect(() => {
    if (!selected) return
    const refreshed = items.find((i) => i.id === selected.id)
    if (refreshed && refreshed !== selected) {
      setSelected(refreshed)
    }
  }, [items, selected])

  useEffect(() => {
    if (!batchView) {
      setBatchCurrents({})
      // Closing the batch dialog must also clear any in-flight amendment so a
      // stale `amendingId` can't carry into the next batch and silently disable
      // its Approve/Reject (GH#1568 guard) with no amend form visible.
      setAmendingId(null)
      setAmendDraft({})
      return
    }
    // Seed funding_round update items with their server-resolved current dates
    // so the diff renders real before-values rather than em-dashes.
    const seeded: Record<number, Record<string, unknown> | null> = {}
    for (const i of batchView.items) {
      if (i.action === 'update' && i.resource_type === 'funding_round') {
        seeded[i.id] = buildFundingRoundBefore(i)
      }
    }
    const targets = batchView.items
      .filter(
        (i) =>
          i.action === 'update' &&
          !!i.target_uuid &&
          i.resource_type !== 'funding_round' &&
          i.resource_type !== 'advisory'
      )
      .map((i) => ({
        id: i.id,
        resource: i.resource_type,
        uuid: i.target_uuid as string,
      }))
    if (targets.length === 0) {
      setBatchCurrents(seeded)
      return
    }
    setBatchCurrentsLoading(true)
    // Cap concurrency so a large batch doesn't fan out N parallel requests
    // (can stress the backend and trigger rate limits).
    const CONCURRENCY = 5
    const runChunked = async () => {
      const next: Record<number, Record<string, unknown> | null> = { ...seeded }
      for (let i = 0; i < targets.length; i += CONCURRENCY) {
        const chunk = targets.slice(i, i + CONCURRENCY)
        const entries = await Promise.all(
          chunk.map((t) =>
            fetchCurrent(t.resource, t.uuid).then(
              (data) => [t.id, data] as const
            )
          )
        )
        for (const [id, data] of entries) next[id] = data
      }
      return next
    }
    runChunked()
      .then((next) => setBatchCurrents(next))
      .finally(() => setBatchCurrentsLoading(false))
  }, [batchView])

  const startAmend = (item: ProposedEdit) => {
    setAmendingId(item.id)
    setAmendDraft({ ...item.payload })
  }

  const cancelAmend = () => {
    setAmendingId(null)
    setAmendDraft({})
  }

  const saveAmend = async (item: ProposedEdit) => {
    setAmendSaving(true)
    try {
      // GH#1592: amendments no longer carry their own comment; reviewers use the
      // review-comment box. Endpoint still accepts an optional `comment`.
      await axios.patch(`${apiUrl}/proposed-edits/${item.id}`, {
        payload: amendDraft,
      })
      cancelAmend()
      suppressSseUntilRef.current = Date.now() + 5000
      await load({ background: true })
    } catch (e: any) {
      const status = e?.response?.status
      const detail = e?.response?.data?.detail
      if (status === 409) {
        cancelAmend()
        suppressSseUntilRef.current = Date.now() + 5000
        await load({ background: true })
        setError(detail ?? 'This edit was reviewed before your amend landed.')
      } else {
        setError(detail ?? e?.message ?? 'Amend failed')
      }
    } finally {
      setAmendSaving(false)
    }
  }

  const reviewItem = async (
    id: number,
    action: 'approve' | 'reject',
    comment?: string
  ) => {
    setItemWorkingId(id)
    try {
      await axios.post(
        `${apiUrl}/proposed-edits/${id}/${action}`,
        comment ? { comment } : {}
      )
      suppressSseUntilRef.current = Date.now() + 5000
      await load({ background: true })
    } catch (e: any) {
      const status = e?.response?.status
      const detail = e?.response?.data?.detail
      if (status === 409) {
        suppressSseUntilRef.current = Date.now() + 5000
        await load({ background: true })
        setError(detail ?? 'This edit was already reviewed by another admin.')
      } else {
        setError(detail ?? e?.message ?? 'Review failed')
      }
    } finally {
      setItemWorkingId(null)
    }
  }

  const runBatchReview = async () => {
    if (!batchView || !batchView.batchId || !batchConfirm) return
    setBatchWorking(true)
    try {
      await axios.post(
        `${apiUrl}/proposed-edits/batches/${batchView.batchId}/${batchConfirm}`,
        batchComment ? { comment: batchComment } : {}
      )
      setBatchConfirm(null)
      setBatchComment('')
      setBatchView(null)
      suppressSseUntilRef.current = Date.now() + 5000
      await load({ background: true })
    } catch (e: any) {
      setError(e?.response?.data?.detail ?? e?.message ?? 'Batch review failed')
    } finally {
      setBatchWorking(false)
    }
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Proposed Edits
      </Typography>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField
              variant="outlined"
              label="Search by ID"
              placeholder="e.g. 1234"
              value={idSearch}
              onChange={(e) => setIdSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') setIdSearchActive(idSearch.trim())
              }}
              helperText={
                idSearchActive
                  ? 'Showing edit(s) by ID + their batch — other filters ignored'
                  : 'Find a review-queue entry (and its batch) by ID'
              }
              sx={{ minWidth: 220 }}
              InputProps={{
                endAdornment: idSearchActive ? (
                  <IconButton
                    size="small"
                    aria-label="Clear ID search"
                    onClick={() => {
                      setIdSearch('')
                      setIdSearchActive('')
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                ) : null,
              }}
            />
            <Button
              variant="outlined"
              onClick={() => setIdSearchActive(idSearch.trim())}
              disabled={!idSearch.trim()}
            >
              Search
            </Button>

            <FormControl
              variant="outlined"
              sx={{ minWidth: 180 }}
              disabled={!!idSearchActive}
            >
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                label="Status"
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="approved">Approved</MenuItem>
                <MenuItem value="rejected">Rejected</MenuItem>
                <MenuItem value="withdrawn">Withdrawn</MenuItem>
              </Select>
            </FormControl>

            <FormControl
              variant="outlined"
              sx={{ minWidth: 180 }}
              disabled={!!idSearchActive}
            >
              <InputLabel>Resource</InputLabel>
              <Select
                value={resourceFilter}
                onChange={(e) => setResourceFilter(e.target.value)}
                label="Resource"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="funder">Funder</MenuItem>
                <MenuItem value="opportunity">Opportunity</MenuItem>
                <MenuItem value="opportunity_questions">
                  Opportunity questions
                </MenuItem>
                <MenuItem value="funding_round">Funding round</MenuItem>
                <MenuItem value="advisory">Advisory</MenuItem>
              </Select>
            </FormControl>

            <FormControl
              variant="outlined"
              sx={{ minWidth: 160 }}
              disabled={!!idSearchActive}
            >
              <InputLabel>International</InputLabel>
              <Select
                value={internationalFilter}
                onChange={(e) => setInternationalFilter(e.target.value)}
                label="International"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="true">International</MenuItem>
                <MenuItem value="false">Domestic</MenuItem>
              </Select>
            </FormControl>

            <FormControl variant="outlined" sx={{ minWidth: 160 }}>
              <InputLabel>Sort</InputLabel>
              <Select
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value as SortOrder)}
                label="Sort"
              >
                <MenuItem value="newest">Newest first</MenuItem>
                <MenuItem value="oldest">Oldest first</MenuItem>
              </Select>
            </FormControl>

            <Autocomplete
              sx={{ minWidth: 280 }}
              options={funderOptions}
              value={funderFilter}
              onChange={(_, value) => setFunderFilter(value)}
              getOptionLabel={(option) => option.name}
              isOptionEqualToValue={(a, b) => a.uuid === b.uuid}
              disabled={!!idSearchActive}
              renderInput={(params) => (
                <TextField {...params} label="Funder" variant="outlined" />
              )}
            />

            <TextField
              variant="outlined"
              label="Opportunity name"
              placeholder="e.g. Community Grant"
              value={oppNameSearch}
              disabled={!!idSearchActive}
              onChange={(e) => setOppNameSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') setOppNameActive(oppNameSearch.trim())
              }}
              helperText="Match by opportunity name (press Enter)"
              sx={{ minWidth: 240 }}
              InputProps={{
                endAdornment: oppNameActive ? (
                  <IconButton
                    size="small"
                    aria-label="Clear opportunity name search"
                    onClick={() => {
                      setOppNameSearch('')
                      setOppNameActive('')
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                ) : null,
              }}
            />

            <Button variant="outlined" onClick={() => load()}>
              Refresh
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* GH#1673: non-disruptive nudge when SSE signals server-side changes,
          instead of auto-reloading the list under an active reviewer. */}
      {pendingUpdates && (
        <Alert
          severity="info"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={() => load()}>
              Refresh
            </Button>
          }
        >
          New review-queue updates are available.
        </Alert>
      )}

      {error && (
        <Typography color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 650 }}>
            <TableHead>
              <TableRow sx={{ '& th': { whiteSpace: 'nowrap' } }}>
                <TableCell>ID</TableCell>
                <TableCell>Resource</TableCell>
                <TableCell>Action</TableCell>
                <TableCell>Target</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Submitted By</TableCell>
                <TableCell>Submitted</TableCell>
                <TableCell>Note</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    No proposed edits found
                  </TableCell>
                </TableRow>
              ) : (
                groups.flatMap((group) => {
                  const rows: React.ReactNode[] = []
                  const isBatched =
                    group.batchId !== null && group.items.length > 1
                  const isExpanded =
                    !isBatched ||
                    (group.batchId !== null &&
                      expandedBatches.has(group.batchId))
                  if (isBatched && group.batchId) {
                    const first = group.items[0]
                    const pendingCount = group.items.filter((i) => i.status === 'pending').length
                    const submittedBy =
                      first.submitted_by_name ?? first.submitted_by_auth0_id
                    const funderName = first.target_funder_name ?? (
                      first.resource_type === 'funder' ? first.target_name : null
                    )
                    const batchId = group.batchId
                    rows.push(
                      <TableRow
                        key={`batch-${batchId}`}
                        hover
                        sx={{ bgcolor: 'action.hover', cursor: 'pointer' }}
                        onClick={() =>
                          setExpandedBatches((prev) => {
                            const next = new Set(prev)
                            if (next.has(batchId)) next.delete(batchId)
                            else next.add(batchId)
                            return next
                          })
                        }
                      >
                        <TableCell colSpan={9}>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 2,
                              flexWrap: 'wrap',
                            }}
                          >
                            <IconButton
                              size="small"
                              aria-label={isExpanded ? 'Collapse batch' : 'Expand batch'}
                              sx={{ ml: -1 }}
                              onClick={(e) => {
                                e.stopPropagation()
                                setExpandedBatches((prev) => {
                                  const next = new Set(prev)
                                  if (next.has(batchId)) next.delete(batchId)
                                  else next.add(batchId)
                                  return next
                                })
                              }}
                            >
                              {isExpanded ? (
                                <KeyboardArrowDownIcon fontSize="small" />
                              ) : (
                                <KeyboardArrowRightIcon fontSize="small" />
                              )}
                            </IconButton>
                            <Typography variant="subtitle2">
                              Batch {batchId.slice(0, 8)}… ·{' '}
                              {group.items.length} edit
                              {group.items.length === 1 ? '' : 's'}
                              {pendingCount < group.items.length &&
                                ` (${pendingCount} pending)`}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              by {submittedBy}
                              {funderName && ` · ${funderName}`}
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    )
                  }
                  if (!isExpanded) return rows
                  for (const item of group.items) {
                    if (item.resource_type === 'advisory') {
                      rows.push(
                        <TableRow
                          key={item.id}
                          sx={
                            group.batchId && group.items.length > 1
                              ? { '& > td:first-of-type': { pl: 4 } }
                              : undefined
                          }
                        >
                          <TableCell>{item.id}</TableCell>
                          <TableCell colSpan={3}>
                            <Chip
                              label="advisory"
                              size="small"
                              color="info"
                              sx={{ mb: 0.5 }}
                            />
                            <AdvisoryDetail item={item} />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={item.status}
                              color={statusColor[item.status]}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {item.submitted_by_name ?? item.submitted_by_auth0_id}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {formatDistanceToNowStrict(
                              new Date(item.created_at + 'Z'),
                              { addSuffix: true },
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography
                              variant="caption"
                              sx={{
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                maxWidth: 240,
                              }}
                            >
                              {item.note ?? ''}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {item.status === 'pending' && (
                              <Button
                                size="small"
                                variant="contained"
                                onClick={() => {
                                  setSelected(item)
                                  setReviewComment('')
                                }}
                              >
                                Review
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      )
                      continue
                    }
                    // GH#1513: funding_round delete rows used to render in a
                    // colSpan=3 cell with a "round delete" chip + monospace
                    // summary, blanking the Resource/Action/Target columns.
                    // They now fall through to the standard layout below —
                    // backend already populates target_name, target_funder_*
                    // and target_opportunity_uuid for delete rows, so
                    // targetHref/targetDisplay produce a proper link and the
                    // funder subtitle renders consistently with opp rows.
                    rows.push(
                      <TableRow
                        key={item.id}
                        sx={
                          group.batchId && group.items.length > 1
                            ? { '& > td:first-of-type': { pl: 4 } }
                            : undefined
                        }
                      >
                    <TableCell>{item.id}</TableCell>
                    <TableCell>{item.resource_type}</TableCell>
                    <TableCell>
                      <Chip
                        label={item.action}
                        color={actionColor[item.action]}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      {(() => {
                        const href = targetHref(item)
                        const label = targetDisplay(item)
                        const nameEl = href ? (
                          <MuiLink
                            component={RouterLink}
                            to={href}
                            onClick={(e) => e.stopPropagation()}
                            underline="hover"
                          >
                            {label}
                          </MuiLink>
                        ) : item.target_name ? (
                          label
                        ) : (
                          <em>new</em>
                        )
                        const survivor = mergeSurvivorDisplay(item)
                        return (
                          <Box>
                            <Typography variant="body2">{nameEl}</Typography>
                            {item.resource_type !== 'funder' &&
                              item.target_funder_name && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  in{' '}
                                  {item.target_funder_uuid ? (
                                    <MuiLink
                                      component={RouterLink}
                                      to={`/funders/${item.target_funder_uuid}`}
                                      onClick={(e) => e.stopPropagation()}
                                      underline="hover"
                                    >
                                      {item.target_funder_name}
                                    </MuiLink>
                                  ) : (
                                    item.target_funder_name
                                  )}
                                </Typography>
                              )}
                            {survivor && (
                              <Typography
                                variant="body2"
                                color="error.main"
                                sx={{ display: 'block' }}
                              >
                                merged into{' '}
                                {item.target_survivor_uuid ? (
                                  <MuiLink
                                    component={RouterLink}
                                    to={
                                      item.resource_type === 'funder'
                                        ? `/funders/${item.target_survivor_uuid}`
                                        : `/opportunities/${item.target_survivor_uuid}`
                                    }
                                    onClick={(e) => e.stopPropagation()}
                                    underline="hover"
                                  >
                                    {survivor}
                                  </MuiLink>
                                ) : (
                                  survivor
                                )}
                              </Typography>
                            )}
                          </Box>
                        )
                      })()}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={item.status}
                        color={statusColor[item.status]}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {item.submitted_by_name ?? item.submitted_by_auth0_id}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {formatDistanceToNowStrict(new Date(item.created_at + 'Z'), {
                        addSuffix: true,
                      })}
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="caption"
                        sx={{
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          maxWidth: 240,
                        }}
                      >
                        {item.note ?? ''}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => {
                          setSelected(item)
                          setReviewComment('')
                        }}
                      >
                        Review
                      </Button>
                    </TableCell>
                  </TableRow>
                    )
                  }
                  return rows
                })
              )}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            // Capped at 200 to match the backend's _list_limit_query
            // (app/api/admin/proposed_edits_admin.py: ge=1, le=200).
            rowsPerPageOptions={[50, 100, 200]}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10))
              setPage(0)
            }}
          />
        </TableContainer>
      )}

      <EditReviewModal
        edit={selected}
        onClose={() => setSelected(null)}
        onChanged={() => {
          // GH#1673: the modal is the primary single-review path; reload in the
          // background so approving/amending never spinner-swaps the table and
          // yanks the reviewer to the top. Suppress the SSE echo of this action.
          suppressSseUntilRef.current = Date.now() + 5000
          return load({ background: true })
        }}
      />


      <Dialog
        open={!!batchView}
        onClose={() => setBatchView(null)}
        maxWidth="lg"
        fullWidth
        scroll="paper"
      >
        {batchView && (
          <>
            <DialogTitle>
              Batch {batchView.batchId?.slice(0, 8)}… ·{' '}
              {batchView.items.length} edit
              {batchView.items.length === 1 ? '' : 's'}
              {(() => {
                const pending = batchView.items.filter(
                  (i) => i.status === 'pending'
                ).length
                return pending < batchView.items.length
                  ? ` · ${pending} pending`
                  : ''
              })()}
            </DialogTitle>
            <DialogContent dividers>
              {(() => {
                const first = batchView.items[0]
                const funderName =
                  first.target_funder_name ??
                  (first.resource_type === 'funder' ? first.target_name : null)
                const funderUuid =
                  first.target_funder_uuid ??
                  (first.resource_type === 'funder' ? first.target_uuid : null)
                return (
                  <Box
                    sx={{
                      display: 'flex',
                      gap: 3,
                      flexWrap: 'wrap',
                      mb: 2,
                      alignItems: 'center',
                    }}
                  >
                    {funderName && (
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Funder
                        </Typography>
                        <Typography variant="body2">
                          {funderUuid ? (
                            <MuiLink
                              component={RouterLink}
                              to={`/funders/${funderUuid}`}
                              underline="hover"
                            >
                              {funderName}
                            </MuiLink>
                          ) : (
                            funderName
                          )}
                        </Typography>
                      </Box>
                    )}
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Submitted by
                      </Typography>
                      <Typography variant="body2">
                        {first.submitted_by_name ?? first.submitted_by_auth0_id}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Submitted
                      </Typography>
                      <Typography variant="body2">
                        {formatDistanceToNowStrict(
                          new Date(first.created_at + 'Z'),
                          { addSuffix: true }
                        )}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block' }}
                      >
                        Batch ID
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ fontFamily: 'monospace', fontSize: 12 }}
                      >
                        {batchView.batchId}
                      </Typography>
                    </Box>
                  </Box>
                )
              })()}

              {batchView.items.map((item) => {
                const isRoundDelete =
                  item.resource_type === 'funding_round' && item.action === 'delete'
                const isMerge = item.action === 'merge'
                const before =
                  item.action === 'update' &&
                  item.resource_type !== 'advisory' &&
                  !isMerge &&
                  !isRoundDelete
                    ? batchCurrents[item.id] ?? null
                    : null
                const rows =
                  item.resource_type !== 'advisory' && !isRoundDelete
                    ? buildDiff(before, item.payload, item.resource_type)
                    : []
                const targetLabel = item.target_name ?? targetDisplay(item)
                const href = targetHref(item)
                const itemWorking = itemWorkingId === item.id
                return (
                  <Paper
                    key={item.id}
                    variant="outlined"
                    sx={{
                      p: 2,
                      mb: 2,
                      borderLeftWidth: 4,
                      borderLeftColor: (() => {
                        if (item.status === 'approved') return 'success.main'
                        if (item.status === 'rejected') return 'error.main'
                        if (item.status === 'withdrawn') return 'text.disabled'
                        return 'warning.main'
                      })(),
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        flexWrap: 'wrap',
                        mb: 1,
                      }}
                    >
                      <Typography variant="subtitle2">
                        #{item.id} · {item.resource_type}/{item.action}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {href ? (
                          <MuiLink
                            component={RouterLink}
                            to={href}
                            underline="hover"
                          >
                            {targetLabel}
                          </MuiLink>
                        ) : (
                          targetLabel
                        )}
                      </Typography>
                      <Chip
                        label={item.status}
                        color={statusColor[item.status]}
                        size="small"
                      />
                      <Box sx={{ flexGrow: 1 }} />
                      {item.status === 'pending' &&
                        amendingId !== item.id && (
                          <>
                            {item.resource_type !== 'advisory' && !isRoundDelete && (
                              <Button
                                size="small"
                                startIcon={<EditIcon fontSize="small" />}
                                disabled={
                                  itemWorking || batchWorking || amendingId !== null
                                }
                                onClick={() => startAmend(item)}
                              >
                                {item.resource_type === 'opportunity_questions'
                                  ? 'Edit questions'
                                  : 'Amend'}
                              </Button>
                            )}
                            <Button
                              size="small"
                              color="error"
                              disabled={
                                itemWorking || batchWorking || amendingId !== null
                              }
                              onClick={() => reviewItem(item.id, 'reject')}
                            >
                              Reject
                            </Button>
                            <Button
                              size="small"
                              variant="contained"
                              color="success"
                              disabled={
                                itemWorking ||
                                batchWorking ||
                                amendingId !== null ||
                                isMerge
                              }
                              onClick={() => reviewItem(item.id, 'approve')}
                            >
                              Approve
                            </Button>
                          </>
                        )}
                    </Box>

                    {item.note && (
                      <Typography
                        variant="body2"
                        sx={{ mb: 1, color: 'text.secondary' }}
                      >
                        {item.note}
                      </Typography>
                    )}

                    <Box
                      sx={{
                        mb: 1.5,
                        p: 1,
                        bgcolor: 'grey.50',
                        borderRadius: 1,
                      }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        Evidence
                      </Typography>
                      <Typography variant="body2">
                        <SafeExternalLink
                          value={item.evidence_url}
                          underline="hover"
                        />
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}
                      >
                        {item.evidence_description}
                      </Typography>
                    </Box>

                    {item.resource_type === 'advisory' ? (
                      <AdvisoryDetail item={item} />
                    ) : isMerge ? (
                      <MergeSummary item={item} summaryOnly />
                    ) : isRoundDelete ? (
                      <RoundDeleteSummary item={item} />
                    ) : amendingId === item.id ? (
                      (() => {
                        const amendErrors = validatePayload(
                          item.resource_type,
                          amendDraft
                        )
                        const hasErrors = Object.keys(amendErrors).length > 0
                        return (
                          <Box
                            sx={{
                              p: 1.5,
                              border: '1px dashed',
                              borderColor: 'primary.main',
                              borderRadius: 1,
                            }}
                          >
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ mb: 1, display: 'block' }}
                            >
                              Amending — edit values or drop fields you don't
                              want applied. Dropped fields will be left
                              unchanged on the target.
                            </Typography>
                            {item.resource_type === 'opportunity_questions' ? (
                              <QuestionsAmendForm
                                value={amendDraft}
                                onChange={setAmendDraft}
                                currentRecord={before}
                                applicationLink={
                                  typeof before?.application_link === 'string'
                                    ? before.application_link
                                    : null
                                }
                              />
                            ) : (
                              <AmendForm
                                resource={item.resource_type}
                                value={amendDraft}
                                errors={amendErrors}
                                onChange={setAmendDraft}
                              />
                            )}
                            <Box
                              sx={{
                                display: 'flex',
                                gap: 1,
                                mt: 1.5,
                                justifyContent: 'flex-end',
                              }}
                            >
                              <Button
                                size="small"
                                onClick={cancelAmend}
                                disabled={amendSaving}
                              >
                                Cancel
                              </Button>
                              <Button
                                size="small"
                                variant="contained"
                                onClick={() => saveAmend(item)}
                                disabled={amendSaving || hasErrors}
                              >
                                {amendSaving ? 'Saving…' : 'Save amendment'}
                              </Button>
                            </Box>
                          </Box>
                        )
                      })()
                    ) : item.resource_type === 'opportunity_questions' ? (
                      <Box
                        sx={{
                          p: 1.5,
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1,
                        }}
                      >
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ mb: 1, display: 'block' }}
                        >
                          Full structured-question replacement. Use "Edit
                          questions" above to amend this proposal before
                          approval.
                        </Typography>
                        {item.status === 'pending' ? (
                          <QuestionsAmendForm
                            value={item.payload}
                            onChange={(next) => {
                              if (batchWorking) return
                              setAmendingId(item.id)
                              setAmendDraft(next)
                            }}
                            currentRecord={before}
                            applicationLink={
                              typeof before?.application_link === 'string'
                                ? before.application_link
                                : null
                            }
                          />
                        ) : (
                          <QuestionsPreview
                            questions={questionsFromPayload(item.payload)}
                          />
                        )}
                      </Box>
                    ) : item.action === 'update' &&
                      batchCurrentsLoading &&
                      !before ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                        <CircularProgress size={20} />
                      </Box>
                    ) : (
                      <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ width: '22%' }}>Field</TableCell>
                              {item.action === 'update' && (
                                <TableCell sx={{ width: '39%' }}>
                                  {diffColumnLabels(item.status).before}
                                </TableCell>
                              )}
                              <TableCell>
                                {diffColumnLabels(item.status).after}
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {rows.length === 0 ? (
                              <TableRow>
                                <TableCell
                                  colSpan={item.action === 'update' ? 3 : 2}
                                  align="center"
                                >
                                  No fields to display
                                </TableCell>
                              </TableRow>
                            ) : (
                              rows.map((row) => (
                                <TableRow
                                  key={row.key}
                                  sx={{
                                    bgcolor: row.changed ? 'warning.50' : undefined,
                                  }}
                                >
                                  <TableCell sx={{ fontWeight: 'bold' }}>
                                    {row.label}
                                  </TableCell>
                                  {item.action === 'update' && (
                                    <TableCell
                                      sx={{
                                        whiteSpace: 'pre-wrap',
                                        verticalAlign: 'top',
                                      }}
                                    >
                                      {renderDiffCell(
                                        row.before,
                                        row.after,
                                        'before',
                                        row.changed,
                                        fieldDefFor(item.resource_type, row.key).type === 'date'
                                          ? formatLongDateNZ
                                          : undefined,
                                        fieldDefFor(item.resource_type, row.key).type === 'url'
                                      )}
                                    </TableCell>
                                  )}
                                  <TableCell
                                    sx={{
                                      whiteSpace: 'pre-wrap',
                                      verticalAlign: 'top',
                                    }}
                                  >
                                    {renderDiffCell(
                                      row.before,
                                      row.after,
                                      'after',
                                      row.changed,
                                      fieldDefFor(item.resource_type, row.key).type === 'date'
                                        ? formatLongDateNZ
                                        : undefined,
                                      fieldDefFor(item.resource_type, row.key).type === 'url'
                                    )}
                                  </TableCell>
                                </TableRow>
                              ))
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}

                    {item.status !== 'pending' && item.review_comment && (
                      <Typography
                        variant="caption"
                        sx={{ display: 'block', mt: 1, color: 'text.secondary' }}
                      >
                        Review comment: {item.review_comment}
                      </Typography>
                    )}
                    {item.status === 'withdrawn' && item.withdraw_reason && (
                      <Typography
                        variant="caption"
                        sx={{ display: 'block', mt: 1, color: 'text.secondary' }}
                      >
                        Withdrawal reason: {item.withdraw_reason}
                      </Typography>
                    )}
                  </Paper>
                )
              })}
            </DialogContent>
            <DialogActions
              sx={{
                flexDirection: 'column',
                alignItems: 'stretch',
                gap: 1,
                p: 2,
              }}
            >
              <TextField
                label="Batch review comment (optional, applied to every pending edit)"
                value={batchComment}
                onChange={(e) => setBatchComment(e.target.value)}
                fullWidth
                multiline
                minRows={2}
                disabled={batchWorking}
              />
              {/* GH#1568: block batch-wide review while an amendment is open so
                  a stray click can't apply the un-amended payloads. */}
              {amendingId !== null && (
                <Typography variant="caption" color="warning.main">
                  Finish or cancel the amendment before reviewing.
                </Typography>
              )}
              {batchHasPendingMerge && (
                <Typography variant="caption" color="warning.main">
                  A merge deletes a record for good, and what it moves and
                  discards is only shown when you open it. Review each merge
                  on its own before approving the rest.
                </Typography>
              )}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: 1,
                }}
              >
                <Button
                  onClick={() => setBatchView(null)}
                  disabled={batchWorking}
                >
                  Close
                </Button>
                {batchView.items.some((i) => i.status === 'pending') && (
                  <>
                    <Button
                      color="error"
                      onClick={() => setBatchConfirm('reject')}
                      disabled={batchWorking || amendingId !== null}
                    >
                      Reject all pending
                    </Button>
                    <Button
                      variant="contained"
                      color="success"
                      onClick={() => setBatchConfirm('approve')}
                      disabled={
                        batchWorking || amendingId !== null || batchHasPendingMerge
                      }
                    >
                      Approve all pending
                    </Button>
                  </>
                )}
              </Box>
            </DialogActions>
          </>
        )}
      </Dialog>

      <Dialog
        open={!!batchConfirm}
        onClose={() => !batchWorking && setBatchConfirm(null)}
        maxWidth="sm"
        fullWidth
      >
        {batchConfirm && batchView && (
          <>
            <DialogTitle>
              {batchConfirm === 'approve' ? 'Approve' : 'Reject'}{' '}
              {
                batchView.items.filter((i) => i.status === 'pending').length
              }{' '}
              pending edit(s)?
            </DialogTitle>
            <DialogContent dividers>
              <Typography variant="body2">
                {batchConfirm === 'approve'
                  ? 'All pending edits in this batch will be applied in a single transaction. If any fails, the whole batch rolls back.'
                  : 'All pending edits in this batch will be rejected with the comment above.'}
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => setBatchConfirm(null)}
                disabled={batchWorking}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color={batchConfirm === 'approve' ? 'success' : 'error'}
                onClick={runBatchReview}
                disabled={batchWorking}
              >
                {batchConfirm === 'approve' ? 'Approve all' : 'Reject all'}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* GH#1622: surface failed review actions (approve/reject/amend/batch) at
          point of action — a bottom snackbar is visible without scrolling back
          to the top-of-page error text, and carries the server 409 detail
          (e.g. the duplicate-opportunity message). */}
      <Snackbar
        open={!!error}
        autoHideDuration={12000}
        onClose={(_e, reason) => {
          if (reason === 'clickaway') return
          setError(null)
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity="error"
          onClose={() => setError(null)}
          sx={{ width: '100%' }}
        >
          {error}
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default ProposedEditsList
